const MAX_SUPPORT_PAGE_URL_CHARACTERS = 2_000;
const SENSITIVE_QUERY_PARAMETER_NAME_PATTERN =
  /^(?:(?:api[-_]?key|key|token|secret|signature|credential|password|authorization|auth|access_token|refresh_token|id_token|oobcode|session|ticket|code|x-amz-).*$|(?:e[-_]?mail|user[-_]?(?:id|email)|uid|account[-_]?id|customer[-_]?(?:id|email)|member[-_]?id|sid|gsessionid|r)$)/i;
const SENSITIVE_FRAGMENT_PATTERN =
  /(?:^|[?&#;])(?:(?:api[-_]?key|key|token|secret|signature|credential|password|authorization|auth|access_token|refresh_token|id_token|oobcode|session|ticket|code|x-amz-)[^=&#;]*|(?:e[-_]?mail|user[-_]?(?:id|email)|uid|account[-_]?id|customer[-_]?(?:id|email)|member[-_]?id|sid|gsessionid|r))=/i;
const TOKEN_BEARING_ROUTE_NAMES = new Set(["auth", "auth-by-token"]);

/**
 * Keep routing evidence useful to support without forwarding credentials that
 * happen to be present in the browser URL. Query credentials, session keys,
 * and direct user identifiers are removed; partner-auth path tokens are
 * replaced; and benign query/fragment context is preserved for reproduction.
 */
export function sanitizeSupportPageUrl(rawUrl: unknown): string | null {
  if (typeof rawUrl !== "string") return null;
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl) return null;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    return null;
  }

  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    return null;
  }

  parsedUrl.username = "";
  parsedUrl.password = "";
  for (const parameterName of [...parsedUrl.searchParams.keys()]) {
    if (SENSITIVE_QUERY_PARAMETER_NAME_PATTERN.test(parameterName)) {
      parsedUrl.searchParams.delete(parameterName);
    }
  }

  const pathSegments = parsedUrl.pathname.split("/");
  if (TOKEN_BEARING_ROUTE_NAMES.has(pathSegments[1] ?? "") && pathSegments[2]) {
    pathSegments[2] = "[REDACTED]";
    parsedUrl.pathname = pathSegments.join("/");
  }

  if (SENSITIVE_FRAGMENT_PATTERN.test(parsedUrl.hash)) {
    parsedUrl.hash = "";
  }

  return parsedUrl.toString().slice(0, MAX_SUPPORT_PAGE_URL_CHARACTERS);
}

/**
 * Resource Timing can include any first- or third-party URL the page touched.
 * Keep only the origin and endpoint path: unlike the current page URL, an
 * arbitrary request's query and fragment have no safe allowlist and routinely
 * contain session IDs, search text, email addresses, and signed credentials.
 */
export function sanitizeSupportNetworkResourceUrl(rawUrl: unknown): string | null {
  const sanitizedPageUrl = sanitizeSupportPageUrl(rawUrl);
  if (!sanitizedPageUrl) return null;

  const parsedUrl = new URL(sanitizedPageUrl);
  parsedUrl.search = "";
  parsedUrl.hash = "";
  return parsedUrl.toString().slice(0, MAX_SUPPORT_PAGE_URL_CHARACTERS);
}
